// core/account/passcode.mjs — plans, signed passcodes and Stripe Payment
// Links, shared by both apps. Each app's src/account.mjs holds its own
// settings (plans, links, salt) and calls createAccount() with them.
//
// Ported from SyncedCue's account.mjs. No server and no keys anywhere.
//
// Passcodes: 12 characters, shown as XXXX-XXXX-XXXX. 60 bits in Crockford
// base-32 (no I, L, O or U):
//
//   version 5 | plan 2 | trial 1 | spare 5 | last day 16 | signature 31
//
// "last day" is days since January 1, 2025. The NAME isn't stored in the
// code: you type it with the code, and the signature covers it.
//
// Honest limit: this runs in the browser, so the signing salt ships with the
// app (scrambled). Passcodes are tamper-evident, not tamper-proof. Making
// them unforgeable needs a server that checks the Stripe payment and signs
// with a private key (see verifyCheckoutOnServer below).

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const HALF = 2 ** 30
const SIG_RANGE = 2 ** 31
const DAY_MS = 86400000
const DAY_EPOCH = Date.UTC(2025, 0, 1)
const MAX_AGE_MS = 2 * 60 * 60 * 1000
const STRIPE_ORIGINS = ['https://checkout.stripe.com', 'https://buy.stripe.com']

export const normalizeName = (name) => String(name || '').trim().replace(/\s+/g, ' ').toLowerCase()
export const cleanCode = (code) => String(code || '').toUpperCase().replace(/[\s-]/g, '').replace(/[IL]/g, '1').replace(/O/g, '0')
export const formatCode = (code) => cleanCode(code).replace(/(.{4})(?=.)/g, '$1-')
export const looksLikePasscode = (code) => cleanCode(code).length === 12
export const daysLeft = (expiresAt, now = Date.now()) => Math.ceil((expiresAt - now) / DAY_MS)
export const formatDate = (ms) => new Date(ms).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
const dayIndex = (ms) => { const d = new Date(ms); return Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - DAY_EPOCH) / DAY_MS) }
const endOfDay = (index) => { const d = new Date(DAY_EPOCH + index * DAY_MS); return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999).getTime() }

function encodeHalf(n) {
  let out = ''
  for (let i = 0; i < 6; i += 1) { out = ALPHABET[n % 32] + out; n = Math.floor(n / 32) }
  return out
}
function decodeHalf(text) {
  let n = 0
  for (const ch of text) {
    const v = ALPHABET.indexOf(ch)
    if (v < 0) return null
    n = n * 32 + v
  }
  return n
}

/**
 * @param {object} cfg
 *   product         'Reframely' | 'SyncedCue Studio' — used in messages
 *   prefix          short signing/storage prefix, e.g. 'cl' or 'sc'
 *   version         0-31, change to invalidate every old passcode
 *   plans           [{ id, name, price, period, days, blurb, features }] — max 4, only add at the end
 *   stripe          { links: { [planId]: url }, customerPortalUrl, requireStripeReferrer }
 *   supportEmail
 *   saltBytes, saltKey   the scrambled salt (scripts/make-salt.js)
 *   trialDays       self-serve trial length, 0 to switch trials off
 *   trialPlan       which plan a self-serve trial opens (default: the last one)
 */
export function createAccount(cfg) {
  const PLANS = cfg.plans
  const STRIPE = cfg.stripe
  const VERSION = cfg.version ?? 21
  const planById = (id) => PLANS.find((p) => p.id === id) || PLANS[PLANS.length - 1]
  const saltBytes = () => new Uint8Array(cfg.saltBytes.map((b, i) => b ^ cfg.saltKey[i % cfg.saltKey.length]))
  const KEYS = {
    marker: `${cfg.prefix}_checkout_started`,
    pending: `${cfg.prefix}_pending_checkout`,
    used: `${cfg.prefix}_used_checkouts`,
    trial: `${cfg.prefix}_trial_used`,
    issued: `${cfg.prefix}_issued_checkouts`,   // checkout id → the passcode it made, so a reload shows it again
    unsaved: `${cfg.prefix}_passcode_unsaved`,  // a new passcode nobody has copied, downloaded or emailed yet
  }

  const paymentsConfigured = () => PLANS.every((p) => Boolean(STRIPE.links[p.id]))
  const onLocalhost = () => typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
  /** 'live' (Stripe), 'test' (no payment, localhost only) or 'off' (links missing on a real domain). */
  const checkoutMode = () => (paymentsConfigured() ? 'live' : onLocalhost() ? 'test' : 'off')

  async function signature(payload, name) {
    const enc = new TextEncoder()
    const key = await crypto.subtle.importKey('raw', enc.encode(`${cfg.prefix}|${payload.toString(16)}|${normalizeName(name)}`), 'PBKDF2', false, ['deriveBits'])
    const b = new Uint8Array(await crypto.subtle.deriveBits({ name: 'PBKDF2', salt: saltBytes(), iterations: 60000, hash: 'SHA-256' }, key, 32))
    return ((b[0] & 0x7f) * 2 ** 24) + (b[1] * 2 ** 16) + (b[2] * 2 ** 8) + b[3]
  }

  async function generatePasscode({ plan, expiresAt, trial = false }, name) {
    const p = PLANS.findIndex((x) => x.id === plan)
    if (p < 0) throw new Error('Unknown plan.')
    if (!normalizeName(name)) throw new Error('A passcode needs a name.')
    const day = Math.max(0, Math.min(65535, dayIndex(expiresAt)))
    const payload = (((VERSION * 4 + p) * 2 + (trial ? 1 : 0)) * 32) * 65536 + day
    const sig = await signature(payload, name)
    const high = payload * 2 + Math.floor(sig / HALF)
    const low = sig % HALF
    return formatCode(encodeHalf(high) + encodeHalf(low))
  }

  async function parsePasscode(code, name) {
    const clean = cleanCode(code)
    const high = clean.length === 12 ? decodeHalf(clean.slice(0, 6)) : null
    const low = clean.length === 12 ? decodeHalf(clean.slice(6)) : null
    if (high === null || low === null) throw new Error('That passcode isn’t valid. It’s 12 letters and numbers — check for typos.')
    const payload = Math.floor(high / 2)
    const sig = (high % 2) * HALF + low
    if (sig >= SIG_RANGE || sig !== (await signature(payload, name))) {
      throw new Error('That passcode doesn’t match the name. Check both for typos.')
    }
    const day = payload % 65536
    let rest = Math.floor(payload / 65536)
    rest = Math.floor(rest / 32)
    const trial = rest % 2 === 1; rest = Math.floor(rest / 2)
    const p = rest % 4; const version = Math.floor(rest / 4)
    if (version !== VERSION || !PLANS[p]) throw new Error('That passcode isn’t valid.')
    return { plan: PLANS[p].id, trial, expiresAt: endOfDay(day) }
  }

  const nextEnd = (plan, currentEnd = 0, now = Date.now()) => Math.max(now, currentEnd) + planById(plan).days * DAY_MS

  async function verifyPasscode(code, name) {
    const d = await parsePasscode(code, name)
    const plan = planById(d.plan)
    if (d.expiresAt < Date.now()) {
      const err = new Error(`${d.trial ? 'This trial' : `This ${plan.name}`} ended on ${formatDate(d.expiresAt)}. Renew to keep using it — your scripts are still here.`)
      err.expired = { code: formatCode(code), name: String(name).trim(), plan: d.plan, trial: d.trial }
      throw err
    }
    return { code: formatCode(code), name: String(name).trim(), plan: d.plan, trial: d.trial, expiresAt: d.expiresAt }
  }

  /* ---- Stripe checkout (Payment Links) ---- */
  const store = (session) => (session ? window.sessionStorage : window.localStorage)
  function readSaved(key, { session = false } = {}) {
    try { const v = store(session).getItem(key); return v == null ? null : JSON.parse(v) } catch { return null }
  }
  function writeSaved(key, value, { session = false } = {}) {
    try {
      if (value == null) store(session).removeItem(key)
      else store(session).setItem(key, JSON.stringify(value))
    } catch { /* private mode */ }
  }
  const referenceId = (...parts) => parts.join('_').replace(/[^A-Za-z0-9_-]+/g, '-').slice(0, 200)

  function startCheckout(link, { pending, email, reference }) {
    const url = new URL(link)
    if (reference) url.searchParams.set('client_reference_id', reference)
    if (email) url.searchParams.set('prefilled_email', email)
    writeSaved(KEYS.pending, { ...pending, ts: Date.now() })
    writeSaved(KEYS.marker, Date.now(), { session: true })
    window.location.assign(url.toString())
  }

  function readCheckoutReturn() {
    const params = new URLSearchParams(window.location.search)
    // The id stays in the address: a reload then shows the same passcode again (see finishCheckout).
    const sessionId = params.get('session_id')
    if (!sessionId || !/^cs_(test_|live_)?[A-Za-z0-9]{10,}$/.test(sessionId)) return { ok: false, reason: 'There’s no Stripe checkout to finish here.' }
    const used = readSaved(KEYS.used) || []
    if (used.includes(sessionId)) {
      // A reload, or coming back to this page: show the same passcode again instead of an error.
      const issued = (readSaved(KEYS.issued) || {})[sessionId]
      if (issued && issued.code) return { ok: true, again: true, sessionId, issued }
      return { ok: false, sessionId, used: true, reason: 'This checkout has already been used to make a passcode.' }
    }
    const started = Number(readSaved(KEYS.marker, { session: true }))
    const pending = readSaved(KEYS.pending)
    const fresh = (t) => Number.isFinite(t) && Date.now() - t < MAX_AGE_MS
    // Privacy browsers (Brave, strict Firefox, some extensions) blank the referrer or replace it with
    // this site's own address, so only a referrer from some other, non-Stripe site counts against it.
    // The real check is the marker and pending order this same tab saved just before going to Stripe.
    let fromStripe = true
    try {
      const ref = document.referrer ? new URL(document.referrer).origin : ''
      if (ref && STRIPE.requireStripeReferrer) fromStripe = STRIPE_ORIGINS.includes(ref) || ref === window.location.origin
    } catch { fromStripe = true }
    if (!fresh(started) || !pending || !fresh(pending.ts) || !fromStripe) {
      return { ok: false, sessionId, reason: 'We couldn’t confirm this return from Stripe in this browser tab.' }
    }
    return { ok: true, sessionId, pending }
  }

  function finishCheckout(sessionId, issued) {
    const used = readSaved(KEYS.used) || []
    writeSaved(KEYS.used, [sessionId, ...used].slice(0, 50))
    if (issued) {
      const all = readSaved(KEYS.issued) || {}
      all[sessionId] = { ...issued, at: Date.now() }
      const keep = Object.entries(all).sort((a, b) => (b[1].at || 0) - (a[1].at || 0)).slice(0, 10)
      writeSaved(KEYS.issued, Object.fromEntries(keep))
    }
    writeSaved(KEYS.pending, null)
    writeSaved(KEYS.marker, null, { session: true })
  }

  async function purchaseDetails(p) {
    if (p.kind === 'trial') return { plan: p.plan, expiresAt: Date.now() + cfg.trialDays * DAY_MS, trial: true }
    if (p.kind === 'new') return { plan: p.plan, expiresAt: nextEnd(p.plan) }
    const d = await parsePasscode(p.code, p.name)   // an ended passcode is fine: that's what renewing is for
    const plan = p.plan || d.plan
    return { plan, expiresAt: nextEnd(plan, d.expiresAt) }
  }

  async function purchase(pending, { applyPurchase, email }) {
    const mode = checkoutMode()
    if (mode === 'test') return { code: await applyPurchase(pending), details: { ...pending, ...(await purchaseDetails(pending)), test: true } }
    if (mode === 'off') {
      const err = new Error(`Online checkout isn’t switched on yet. Email ${cfg.supportEmail} and we’ll set up your ${planById(pending.plan).name} today.`)
      err.offline = true
      throw err
    }
    startCheckout(STRIPE.links[pending.plan], {
      pending,
      email: email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email.trim() : undefined,
      reference: referenceId(cfg.prefix, pending.kind, pending.plan, pending.name),
    })
    return null
  }

  /** Self-serve trial: one per browser. Returns null when trials are off or already used here. */
  const trialAvailable = () => cfg.trialDays > 0 && !readSaved(KEYS.trial)
  const markTrialUsed = () => writeSaved(KEYS.trial, Date.now())

  /** A just-made passcode stays flagged until it's copied, downloaded, emailed or ticked as saved. */
  const markPasscodeUnsaved = (code) => writeSaved(KEYS.unsaved, formatCode(code))
  const markPasscodeSaved = () => writeSaved(KEYS.unsaved, null)
  const passcodeUnsaved = (code) => !!code && readSaved(KEYS.unsaved) === formatCode(code)

  const hasFeature = (session, feature) => {
    if (!session) return false
    if (session.admin) return true
    const plan = planById(session.plan)
    return !plan.unlocks || plan.unlocks.includes(feature)
  }

  return {
    product: cfg.product, prefix: cfg.prefix, PLANS, STRIPE, SUPPORT_EMAIL: cfg.supportEmail, trialDays: cfg.trialDays || 0,
    trialPlan: cfg.trialPlan || PLANS[PLANS.length - 1].id,
    planById, paymentsConfigured, checkoutMode, generatePasscode, parsePasscode, verifyPasscode, nextEnd,
    readSaved, writeSaved, startCheckout, readCheckoutReturn, finishCheckout, purchaseDetails, purchase,
    trialAvailable, markTrialUsed, hasFeature, markPasscodeUnsaved, markPasscodeSaved, passcodeUnsaved,
    looksLikePasscode, formatCode, cleanCode, daysLeft, formatDate, normalizeName,
  }
}
